/**
 * Reconciliación escalable del MAESTRO SaaS · UI (feb 2026)
 *
 * Sustituye funcionalmente al flujo "Validar → Importar" cuando ya existen
 * registros previos. Presenta 5 sub-pantallas:
 *
 *   1) Subida y análisis
 *   2) Resumen del análisis
 *   3) Revisión de cambios (aprobar/rechazar granular y masivo)
 *   4) Confirmación y commit (super_admin)
 *   5) Informe de resultados + descarga
 *
 * Regla clave: cuando la única diferencia es la asociación con STOCK, se
 * muestra `ASOCIACION_STOCK` con valor anterior y nuevo, y se ofrece un
 * chip masivo específico. Nunca se sobreescribe raw_maestro por STOCK.
 *
 * Todos los elementos interactivos y de datos críticos llevan `data-testid`.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { UploadCloud, CheckCircle2, AlertTriangle, ChevronDown, ChevronRight, Download } from 'lucide-react';
import { saasApi, errText } from './api';

const ETAPAS = ['upload', 'resumen', 'revision', 'commit', 'informe'];

export default function MaestroReconciliation() {
  const [etapa, setEtapa] = useState('upload');
  const [maestro, setMaestro] = useState(null);
  const [stock, setStock] = useState(null);
  const [sesion, setSesion] = useState(null); // objeto sesión
  const [loading, setLoading] = useState(false);
  const [confirm, setConfirm] = useState('');
  const [commitResult, setCommitResult] = useState(null);
  const [expand, setExpand] = useState({});

  const irA = (e) => { if (ETAPAS.includes(e)) setEtapa(e); };

  const analizar = async () => {
    if (!maestro) return toast.error('Selecciona el MAESTRO normalizado');
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('maestro', maestro);
      if (stock) fd.append('stock', stock);
      const r = await saasApi.post('/saas/admin/importacion-maestro/analizar', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSesion(r.data);
      setCommitResult(null);
      setConfirm('');
      setEtapa('resumen');
      toast.success('Análisis completado');
    } catch (e) { toast.error(errText(e)); } finally { setLoading(false); }
  };

  const recargarSesion = async () => {
    if (!sesion?.session_id) return;
    try {
      const r = await saasApi.get(`/saas/admin/importacion-maestro/sesion/${sesion.session_id}`);
      setSesion({ ...sesion, ...r.data });
    } catch (e) { toast.error(errText(e)); }
  };

  const decidirFila = async (fila_origen, decision) => {
    setLoading(true);
    try {
      await saasApi.post(
        `/saas/admin/importacion-maestro/sesion/${sesion.session_id}/decidir`,
        { decisiones: [{ fila_origen, decision }] },
      );
      await recargarSesion();
    } catch (e) { toast.error(errText(e)); } finally { setLoading(false); }
  };

  const decidirMasivo = async (grupo, decision, opts = {}) => {
    setLoading(true);
    try {
      const body = { grupo, decision };
      if (opts.confirmacion_reactivaciones_count != null) {
        body.confirmacion_reactivaciones_count = opts.confirmacion_reactivaciones_count;
      }
      const r = await saasApi.post(
        `/saas/admin/importacion-maestro/sesion/${sesion.session_id}/decidir-masivo`,
        body,
      );
      toast.success(`${decision}: ${r.data.aplicadas} filas`);
      await recargarSesion();
    } catch (e) { toast.error(errText(e)); } finally { setLoading(false); }
  };

  const commit = async () => {
    setLoading(true);
    try {
      const r = await saasApi.post(
        `/saas/admin/importacion-maestro/sesion/${sesion.session_id}/commit`,
        { confirmacion: confirm },
      );
      setCommitResult(r.data);
      toast.success(`Aplicación ${r.data.estado_ejecucion}`);
      setEtapa('informe');
      await recargarSesion();
    } catch (e) { toast.error(errText(e)); } finally { setLoading(false); }
  };

  const descargarInforme = (formato) => {
    if (!sesion?.session_id) return;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/saas/admin/importacion-maestro/sesion/${sesion.session_id}/informe.${formato}`;
    window.open(url, '_blank');
  };

  const consecuencias = useMemo(() => {
    if (!sesion) return null;
    const dec = sesion.decisiones || [];
    return {
      aprobadas: dec.filter(d => d.decision === 'APROBADA').length,
      rechazadas: dec.filter(d => d.decision === 'RECHAZADA').length,
      pendientes: dec.filter(d => d.decision === 'PENDIENTE').length,
      nuevas: dec.filter(d => d.clasificacion === 'NUEVA' && d.decision === 'APROBADA').length,
      actualizaciones: dec.filter(d => (d.clasificacion === 'MODIFICADA' || d.clasificacion === 'RECONCILIABLE_SIN_HASH_CON_DIFERENCIAS') && d.decision === 'APROBADA').length,
      selladas: dec.filter(d => d.clasificacion === 'RECONCILIABLE_SIN_HASH_EQUIVALENTE' && d.decision === 'APROBADA').length,
      bloqueadas: (sesion.stats?.bloqueadas_idempotencia || 0)
        + (sesion.stats?.bloqueadas_duplicado_archivo || 0)
        + (sesion.stats?.bloqueadas_cliente_cif || 0)
        + (sesion.stats?.bloqueadas_estado_destino || 0),
    };
  }, [sesion]);

  const puedeCommit = consecuencias
    && consecuencias.pendientes === 0
    && consecuencias.bloqueadas === 0
    && confirm.trim() === 'IMPORTAR MAESTRO';

  return (
    <div className="space-y-5" data-testid="maestro-reconciliation-root">
      <StepBar etapa={etapa} onGo={irA} sesion={sesion} commitResult={commitResult} />

      {etapa === 'upload' && (
        <Card>
          <CardHeader icon={<UploadCloud className="w-6 h-6" />} title="Análisis del MAESTRO"
            desc="Sube el MAESTRO (y opcionalmente STOCK). El análisis clasifica cada fila: nueva, idéntica, modificada, reconciliable sin hash, bloqueada. Nada se escribe todavía." />
          <div className="grid md:grid-cols-2 gap-4 mt-4">
            <Field label="MAESTRO normalizado (.xlsx) *">
              <input data-testid="reco-file-maestro" className="input" type="file" accept=".xlsx,.xlsm"
                onChange={e => setMaestro(e.target.files?.[0] || null)} />
            </Field>
            <Field label="STOCK dispositivos (.xlsm) — opcional">
              <input data-testid="reco-file-stock" className="input" type="file" accept=".xlsx,.xlsm"
                onChange={e => setStock(e.target.files?.[0] || null)} />
            </Field>
          </div>
          <div className="flex justify-end mt-4">
            <button data-testid="reco-btn-analizar" className="btn-primary" disabled={loading || !maestro} onClick={analizar}>
              {loading ? 'Analizando…' : 'Analizar'}
            </button>
          </div>
        </Card>
      )}

      {etapa === 'resumen' && sesion && (
        <ResumenAnalisis
          sesion={sesion}
          consecuencias={consecuencias}
          onRevisar={() => setEtapa('revision')}
          onConfirmar={() => setEtapa('commit')}
          onDescargar={descargarInforme}
        />
      )}

      {etapa === 'revision' && sesion && (
        <Revision
          sesion={sesion}
          loading={loading}
          expand={expand} setExpand={setExpand}
          onDecidir={decidirFila}
          onDecidirMasivo={decidirMasivo}
          onVolver={() => setEtapa('resumen')}
          onSiguiente={() => setEtapa('commit')}
          consecuencias={consecuencias}
        />
      )}

      {etapa === 'commit' && sesion && (
        <Confirmacion
          sesion={sesion} consecuencias={consecuencias}
          confirm={confirm} setConfirm={setConfirm}
          onCommit={commit} loading={loading} puedeCommit={puedeCommit}
          onVolver={() => setEtapa('revision')}
        />
      )}

      {etapa === 'informe' && sesion && commitResult && (
        <Informe result={commitResult} sesion={sesion} onDescargar={descargarInforme} />
      )}
    </div>
  );
}

// --------------------------------------------------------------------------
// Subcomponentes
// --------------------------------------------------------------------------
function StepBar({ etapa, onGo, sesion, commitResult }) {
  const items = [
    ['upload', '1 · Subida'],
    ['resumen', '2 · Resumen'],
    ['revision', '3 · Revisión'],
    ['commit', '4 · Confirmación'],
    ['informe', '5 · Informe'],
  ];  return (
    <div className="flex gap-2 flex-wrap" data-testid="reco-stepbar">
      {items.map(([k, l]) => {
        const habilitada = k === 'upload'
          || (k === 'resumen' && !!sesion)
          || (k === 'revision' && !!sesion)
          || (k === 'commit' && !!sesion)
          || (k === 'informe' && !!commitResult);
        const activa = etapa === k;
        return (
          <button key={k} data-testid={`reco-step-${k}`}
            disabled={!habilitada}
            onClick={() => habilitada && onGo(k)}
            className={`px-3 py-2 rounded-xl text-sm border ${activa ? 'bg-[#0a6640] text-white border-[#0a6640]' : habilitada ? 'bg-white' : 'bg-slate-50 text-slate-400'}`}>
            {l}
          </button>
        );
      })}
    </div>
  );
}

function ResumenAnalisis({ sesion, consecuencias, onRevisar, onConfirmar, onDescargar }) {
  const s = sesion.stats || {};
  const c = consecuencias || {};
  const tarjetas = [
    ['Total filas archivo', s.total, 'total'],
    ['Nuevas', s.nuevas, 'nuevas'],
    ['Idénticas', s.identicas, 'identicas'],
    ['Modificadas', s.modificadas, 'modificadas'],
    ['Solo STOCK', s.solo_asociacion_stock || 0, 'solo-stock'],
    ['Pre-hash equivalentes', s.reconciliables_sin_hash_equivalentes || 0, 'equivalentes'],
    ['Pre-hash con diferencias', s.reconciliables_sin_hash_con_diferencias || 0, 'con-dif'],
    ['Ausentes en archivo', s.ausentes_en_archivo || 0, 'ausentes'],
    ['Bloqueadas cliente', s.bloqueadas_cliente_cif, 'bloq-cli'],
    ['Bloqueadas idempotencia', s.bloqueadas_idempotencia, 'bloq-idm'],
    ['Bloqueadas duplicado', s.bloqueadas_duplicado_archivo, 'bloq-dup'],
    ['Bloqueadas estado_destino', s.bloqueadas_estado_destino, 'bloq-est'],
  ];
  const barra = c.bloqueadas > 0 ? 'rojo' : c.pendientes > 0 ? 'ambar' : 'verde';
  const cls = { rojo: 'bg-rose-50 border-rose-300 text-rose-800',
                ambar: 'bg-amber-50 border-amber-300 text-amber-900',
                verde: 'bg-emerald-50 border-emerald-300 text-emerald-800' }[barra];
  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-black text-lg">Resumen del análisis</h3>
        <span className="text-xs text-slate-500" data-testid="reco-session-id">Sesión: {sesion.session_id}</span>
      </div>
      <div className={`border rounded-xl p-3 mb-4 ${cls}`} data-testid="reco-status-banner">
        {barra === 'rojo' && <><b>Bloqueado</b>: hay {c.bloqueadas} filas bloqueadas. Corrige el archivo y reanaliza.</>}
        {barra === 'ambar' && <><b>Cambios pendientes de decisión</b>: {c.pendientes} filas. Revisa y aprueba o rechaza antes del commit.</>}
        {barra === 'verde' && <><b>Todo verde</b>: sin cambios pendientes ni bloqueos. Puedes proceder al commit.</>}
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {tarjetas.map(([l, v, tid]) => (
          <div key={l} data-testid={`reco-stat-${tid}`} className="border rounded-xl p-3">
            <div className="text-xs text-slate-500">{l}</div>
            <div className="text-2xl font-black">{v ?? 0}</div>
          </div>
        ))}
      </div>
      {!!sesion.ausentes_en_archivo?.length && (
        <div className="mt-5">
          <p className="font-bold mb-2">Registros existentes ausentes en el archivo (no se dan de baja)</p>
          <Simple data-testid="reco-tabla-ausentes"
            headers={['Fila', 'Estado', 'Import ID original']}
            rows={sesion.ausentes_en_archivo.slice(0, 40).map(a => [a.fila_origen, a.estado || '—', a.import_id_original || '—'])} />
        </div>
      )}
      <div className="flex flex-wrap gap-2 mt-5">
        <button data-testid="reco-btn-descargar-analisis-json" className="btn-secondary" onClick={() => onDescargar('json')}>
          <Download className="w-4 h-4 inline" /> JSON
        </button>
        <button data-testid="reco-btn-descargar-analisis-csv" className="btn-secondary" onClick={() => onDescargar('csv')}>
          <Download className="w-4 h-4 inline" /> CSV
        </button>
        <div className="ml-auto flex gap-2">
          <button data-testid="reco-btn-revisar" className="btn-primary"
            disabled={c.pendientes === 0} onClick={onRevisar}>
            Revisar cambios ({c.pendientes})
          </button>
          <button data-testid="reco-btn-continuar" className="btn-primary"
            disabled={c.pendientes > 0 || c.bloqueadas > 0} onClick={onConfirmar}>
            Continuar a aplicar cambios
          </button>
        </div>
      </div>
    </Card>
  );
}

function EstadoGrupoChip({ nP, nA, nR }) {
  const total = nP + nA + nR;
  if (total === 0) return null;
  let label = 'Pendiente', cls = 'bg-amber-100 text-amber-800 border-amber-300';
  if (nP === 0 && nA > 0 && nR === 0) { label = 'Aprobado'; cls = 'bg-emerald-100 text-emerald-800 border-emerald-300'; }
  else if (nP === 0 && nA === 0 && nR > 0) { label = 'Rechazado'; cls = 'bg-rose-100 text-rose-800 border-rose-300'; }
  else if (nP === 0 && nA > 0 && nR > 0) { label = 'Parcial'; cls = 'bg-sky-100 text-sky-800 border-sky-300'; }
  else if (nP > 0 && (nA > 0 || nR > 0)) { label = 'Parcial'; cls = 'bg-sky-100 text-sky-800 border-sky-300'; }
  return <span data-testid="grupo-estado-chip" className={`text-[10px] px-2 py-0.5 rounded font-bold uppercase border ${cls}`}>{label}</span>;
}

function ContadoresFila({ nP, nA, nR }) {
  return (
    <span className="text-xs text-slate-500 space-x-2" data-testid="grupo-contadores">
      <span title="Aprobadas" className="text-emerald-700">✓{nA}</span>
      <span title="Rechazadas" className="text-rose-700">✗{nR}</span>
      <span title="Pendientes" className="text-amber-700">•{nP}</span>
    </span>
  );
}

function Revision({ sesion, loading, expand, setExpand, onDecidir, onDecidirMasivo, onVolver, onSiguiente, consecuencias }) {
  const cambios = sesion.cambios || [];
  const decisiones = sesion.decisiones || [];
  const decIndex = useMemo(() => Object.fromEntries(decisiones.map(d => [d.fila_origen, d])), [decisiones]);
  const grupos = sesion.grupos_categorias || [];
  const desglose = sesion.desglose_por_campo || [];
  const [openGroup, setOpenGroup] = React.useState(null);
  // Ajuste 3 · tarjetas superiores como filtro de decisión.
  const [filtroDecision, setFiltroDecision] = React.useState('TODAS'); // TODAS | PENDIENTE | APROBADA | RECHAZADA
  // Ajuste 1 · modal de confirmación aprobación/rechazo masivo de reactivaciones.
  const [confirmReactivar, setConfirmReactivar] = React.useState(null); // { decision, count }

  const totales = useMemo(() => ({
    total: decisiones.length,
    pendientes: decisiones.filter(d => d.decision === 'PENDIENTE').length,
    aprobadas: decisiones.filter(d => d.decision === 'APROBADA').length,
    rechazadas: decisiones.filter(d => d.decision === 'RECHAZADA').length,
  }), [decisiones]);

  // Contadores por grupo (aprobadas / rechazadas / pendientes)
  const contadoresGrupo = useMemo(() => {
    const acc = {};
    decisiones.forEach(d => {
      const k = d.categoria_grupo;
      if (!acc[k]) acc[k] = { nP: 0, nA: 0, nR: 0 };
      if (d.decision === 'APROBADA') acc[k].nA++;
      else if (d.decision === 'RECHAZADA') acc[k].nR++;
      else acc[k].nP++;
    });
    return acc;
  }, [decisiones]);

  const gpe = (clave) => contadoresGrupo[clave] || { nP: 0, nA: 0, nR: 0 };

  // Filtrado combinado: grupo abierto + tarjeta seleccionada.
  const filasVisibles = useMemo(() => {
    let base = openGroup
      ? cambios.filter(c => decIndex[c.fila]?.categoria_grupo === openGroup)
      : cambios;
    if (filtroDecision !== 'TODAS') {
      base = base.filter(c => (decIndex[c.fila]?.decision || 'PENDIENTE') === filtroDecision);
    }
    return base;
  }, [openGroup, cambios, filtroDecision, decIndex]);

  const gruposSensibles = grupos.filter(g => g.sensible);
  const gruposPuros = grupos.filter(g => !g.es_varios && !g.sensible && g.clave !== 'NUEVA' && g.accionable);
  const gruposVarios = grupos.filter(g => g.es_varios);

  const nPendReactivaciones = gpe('REACTIVACION_BAJA').nP;

  const solicitarReactivacionMasiva = (decision) => {
    if (nPendReactivaciones === 0) return;
    setConfirmReactivar({ decision, count: nPendReactivaciones });
  };
  const ejecutarReactivacionMasiva = async () => {
    if (!confirmReactivar) return;
    const { decision, count } = confirmReactivar;
    setConfirmReactivar(null);
    await onDecidirMasivo('REACTIVACION_BAJA', decision, { confirmacion_reactivaciones_count: count });
  };

  return (
    <Card>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-black text-lg">Revisión de cambios</h3>
        <button className="btn-secondary" onClick={onVolver}>Volver al resumen</button>
      </div>

      {/* Franja permanente de contadores — sticky · ahora ACTÚA COMO FILTRO */}
      <div className="sticky top-0 z-30 -mx-5 px-5 pt-1 pb-3 mb-4 bg-white/95 backdrop-blur border-b border-slate-200" data-testid="reco-contadores-sticky">
        <div className="grid grid-cols-4 gap-2" data-testid="reco-contadores">
          <StatBox label="Total" value={totales.total} tone="slate" tid="total"
            selected={filtroDecision === 'TODAS'} onClick={() => setFiltroDecision('TODAS')} />
          <StatBox label="Pendientes" value={totales.pendientes} tone="amber" tid="pendientes"
            selected={filtroDecision === 'PENDIENTE'} onClick={() => setFiltroDecision('PENDIENTE')} />
          <StatBox label="Aprobadas" value={totales.aprobadas} tone="emerald" tid="aprobadas"
            selected={filtroDecision === 'APROBADA'} onClick={() => setFiltroDecision('APROBADA')} />
          <StatBox label="Rechazadas" value={totales.rechazadas} tone="rose" tid="rechazadas"
            selected={filtroDecision === 'RECHAZADA'} onClick={() => setFiltroDecision('RECHAZADA')} />
        </div>
        {filtroDecision !== 'TODAS' && (
          <div className="mt-2 text-xs text-slate-500 flex items-center gap-2" data-testid="reco-filtro-activo">
            Filtrando por decisión: <b className="text-slate-800">{filtroDecision}</b>
            <button className="link" onClick={() => setFiltroDecision('TODAS')}>Quitar filtro</button>
          </div>
        )}
      </div>

      {!!gruposSensibles.length && (
        <div className="border-2 border-amber-500 rounded-xl p-3 mb-4 bg-amber-50" data-testid="reco-panel-reactivaciones">
          <div className="flex items-start gap-3 mb-2">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-bold text-sm text-amber-900">Reactivaciones sensibles · BAJA → ACTIVA/PIPELINE</p>
                <EstadoGrupoChip {...gpe('REACTIVACION_BAJA')} />
              </div>
              <p className="text-xs text-amber-800 mt-1">
                Puedes decidir cada fila individualmente o <b>aprobar/rechazar conjuntamente</b> las pendientes.
                La aprobación conjunta requiere confirmar el número exacto de suscripciones que se reactivarán.
              </p>
            </div>
          </div>
          {gruposSensibles.map(g => {
            const c = gpe(g.clave);
            return (
              <div key={g.clave} data-testid={`reco-grupo-${g.clave}`} className="border border-amber-300 rounded-lg px-3 py-2 flex items-center gap-2 text-sm bg-white flex-wrap">
                <span className="font-medium text-amber-900">{g.label}</span>
                <span className="text-slate-500">· {g.n_filas} filas</span>
                <ContadoresFila {...c} />
                <button className="link" data-testid={`reco-ver-${g.clave}`} onClick={() => setOpenGroup(openGroup === g.clave ? null : g.clave)}>
                  {openGroup === g.clave ? 'Cerrar' : 'Revisar una a una'}
                </button>
                <button className="btn-primary py-1 px-2 text-xs bg-amber-600 hover:bg-amber-700"
                  data-testid={`reco-aprobar-masivo-${g.clave}`}
                  disabled={loading || c.nP === 0}
                  onClick={() => solicitarReactivacionMasiva('APROBADA')}>
                  Aprobar todas ({c.nP})
                </button>
                <button className="btn-secondary py-1 px-2 text-xs"
                  data-testid={`reco-rechazar-masivo-${g.clave}`}
                  disabled={loading || c.nP === 0}
                  onClick={() => solicitarReactivacionMasiva('RECHAZADA')}>
                  Rechazar todas ({c.nP})
                </button>
              </div>
            );
          })}
        </div>
      )}

      {!!gruposPuros.length && (
        <div className="border rounded-xl p-3 mb-4" data-testid="reco-panel-grupos-puros">
          <p className="font-bold text-sm mb-2">Grupos por tipo de cambio</p>
          <div className="flex flex-wrap gap-2">
            {gruposPuros.map(g => {
              const c = gpe(g.clave);
              return (
                <div key={g.clave} data-testid={`reco-grupo-${g.clave}`} className="border rounded-lg px-3 py-2 flex items-center gap-2 text-sm bg-white flex-wrap">
                  <span className="font-medium">{g.label}</span>
                  <EstadoGrupoChip {...c} />
                  <span className="text-slate-400">· {g.n_filas} filas</span>
                  <ContadoresFila {...c} />
                  <button className="link" data-testid={`reco-ver-${g.clave}`} onClick={() => setOpenGroup(openGroup === g.clave ? null : g.clave)}>
                    {openGroup === g.clave ? 'Cerrar' : 'Ver filas'}
                  </button>
                  <button className="btn-primary py-1 px-2 text-xs"
                    data-testid={`reco-aprobar-masivo-${g.clave}`}
                    disabled={loading || c.nP === 0}
                    onClick={() => onDecidirMasivo(g.clave, 'APROBADA')}>Aprobar {c.nP} pendientes</button>
                  <button className="btn-secondary py-1 px-2 text-xs"
                    data-testid={`reco-rechazar-masivo-${g.clave}`}
                    disabled={loading || c.nP === 0}
                    onClick={() => onDecidirMasivo(g.clave, 'RECHAZADA')}>Rechazar {c.nP} pendientes</button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!!gruposVarios.length && (
        <div className="border rounded-xl p-3 mb-4 bg-amber-50/40" data-testid="reco-panel-grupos-varios">
          <p className="font-bold text-sm mb-2">Varios cambios (combinaciones exactas)</p>
          <div className="flex flex-wrap gap-2">
            {gruposVarios.map(g => {
              const c = gpe(g.clave);
              return (
                <div key={g.clave} data-testid={`reco-grupo-${g.clave}`} className="border rounded-lg px-3 py-2 flex items-center gap-2 text-sm bg-white flex-wrap">
                  <span className="font-medium">{g.label}</span>
                  <EstadoGrupoChip {...c} />
                  <span className="text-slate-400">· {g.n_filas} filas</span>
                  <ContadoresFila {...c} />
                  <button className="link" data-testid={`reco-ver-${g.clave}`} onClick={() => setOpenGroup(openGroup === g.clave ? null : g.clave)}>
                    {openGroup === g.clave ? 'Cerrar' : 'Ver filas'}
                  </button>
                  <button className="btn-primary py-1 px-2 text-xs"
                    data-testid={`reco-aprobar-masivo-${g.clave}`}
                    disabled={loading || c.nP === 0}
                    onClick={() => onDecidirMasivo(g.clave, 'APROBADA')}>Aprobar {c.nP}</button>
                  <button className="btn-secondary py-1 px-2 text-xs"
                    data-testid={`reco-rechazar-masivo-${g.clave}`}
                    disabled={loading || c.nP === 0}
                    onClick={() => onDecidirMasivo(g.clave, 'RECHAZADA')}>Rechazar {c.nP}</button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!!desglose.length && (
        <div className="border rounded-xl p-3 mb-4" data-testid="reco-desglose-campos">
          <p className="font-bold text-sm mb-2">Resumen por campo (informativo)</p>
          <div className="flex flex-wrap gap-2 text-xs">
            {desglose.map(x => (
              <span key={x.campo} data-testid={`reco-desglose-${x.campo}`} className="bg-slate-100 border border-slate-200 rounded-md px-2 py-1">
                <b>{x.n_filas}</b> · {x.campo}
              </span>
            ))}
          </div>
        </div>
      )}

      {openGroup && (
        <div className="mb-2 text-sm text-slate-600" data-testid="reco-grupo-abierto">
          Mostrando filas del grupo: <b>{grupos.find(g => g.clave === openGroup)?.label || openGroup}</b> ({filasVisibles.length} filas)
          <button className="link ml-2" onClick={() => setOpenGroup(null)}>Ver todas</button>
        </div>
      )}
      <div className="border rounded-xl overflow-auto">
        <table className="table">
          <thead><tr>
            <th className="w-8"></th>
            <th>Fila</th>
            <th>Contexto funcional</th>
            <th>Cambios</th>
            <th>Decisión</th><th className="text-right">Acción</th>
          </tr></thead>
          <tbody>
            {filasVisibles.length ? filasVisibles.map(c => {
              const d = decIndex[c.fila] || {};
              const abierto = !!expand[c.fila];
              const esSensible = d.sensible === true;
              const ctx = d.contexto_funcional || {};
              return (
                <React.Fragment key={c.fila}>
                  <tr data-testid={`reco-fila-${c.fila}`} className={esSensible ? 'bg-amber-50/50' : ''}>
                    <td className="align-top pt-3">
                      <button onClick={() => setExpand({ ...expand, [c.fila]: !abierto })}>
                        {abierto ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </button>
                    </td>
                    <td className="font-mono text-xs align-top pt-3">
                      {c.fila}
                      {esSensible && (
                        <div data-testid={`reco-sensible-${c.fila}`} className="mt-1 inline-flex items-center gap-1 text-[10px] bg-amber-500 text-white px-2 py-0.5 rounded font-bold">
                          <AlertTriangle className="w-3 h-3" /> REACTIVACIÓN
                        </div>
                      )}
                      {c.marca === 'SOLO_ASOCIACION_STOCK' && (
                        <div className="mt-1 inline-block text-[10px] bg-sky-100 text-sky-700 px-2 py-0.5 rounded">Solo STOCK</div>
                      )}
                    </td>
                    <td className="text-xs" data-testid={`reco-contexto-${c.fila}`}>
                      <ContextoFuncional ctx={ctx} />
                    </td>
                    <td className="text-xs">
                      <CambiosResumen diferencias={c.diferencias || []} ctx={ctx} />
                    </td>
                    <td className="align-top pt-3"><DecisionBadge d={d.decision} /></td>
                    <td className="text-right space-x-2 align-top pt-3">
                      <button className={esSensible ? 'btn-primary py-1 px-2 text-xs bg-amber-600 hover:bg-amber-700' : 'link'}
                        data-testid={`reco-aprobar-${c.fila}`} disabled={loading}
                        onClick={() => onDecidir(c.fila, 'APROBADA')}>
                        {esSensible ? 'Aprobar reactivación' : 'Aprobar'}
                      </button>
                      <button className={esSensible ? 'btn-secondary py-1 px-2 text-xs' : 'link'}
                        data-testid={`reco-rechazar-${c.fila}`} disabled={loading}
                        onClick={() => onDecidir(c.fila, 'RECHAZADA')}>
                        {esSensible ? 'Rechazar reactivación' : 'Rechazar'}
                      </button>
                    </td>
                  </tr>
                  {abierto && (
                    <tr><td colSpan={6} className="bg-slate-50">
                      <div className="p-3">
                        <p className="font-bold text-xs text-slate-600 mb-2">Detalle técnico (diff campo a campo)</p>
                        <table className="table">
                          <thead><tr><th>Campo</th><th>Valor anterior</th><th>Valor nuevo</th></tr></thead>
                          <tbody>
                            {(c.diferencias || []).map((df, i) => (
                              <tr key={i} data-testid={`diff-row-${c.fila}-${df.campo}`}>
                                <td className="font-mono text-xs">{df.campo === 'ASOCIACION_STOCK' ? 'Asociación STOCK' : df.campo}</td>
                                <td>{fmtAsocStock(df, 'anterior')}</td>
                                <td>{fmtAsocStock(df, 'nuevo')}</td>
                              </tr>
                            ))}
                            {!c.diferencias?.length && <tr><td colSpan={3} className="text-slate-400 text-center py-4">Sin diferencias funcionales — sellado de hash</td></tr>}
                          </tbody>
                        </table>
                      </div>
                    </td></tr>
                  )}
                </React.Fragment>
              );
            }) : <tr><td colSpan={6} className="text-center text-slate-400 py-10">No hay cambios pendientes</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="flex justify-between mt-4 items-center">
        <div className="text-sm text-slate-600" data-testid="reco-consecuencias">
          <b>Consecuencias al aplicar:</b> {consecuencias?.nuevas || 0} nuevas · {consecuencias?.actualizaciones || 0} actualizaciones · {consecuencias?.selladas || 0} selladas · {consecuencias?.rechazadas || 0} rechazadas · <b>{consecuencias?.pendientes || 0} pendientes</b>
        </div>
        <button data-testid="reco-btn-siguiente" className="btn-primary"
          disabled={(consecuencias?.pendientes || 0) > 0} onClick={onSiguiente}>
          Continuar a aplicar cambios
        </button>
      </div>

      {/* Modal · confirmación aprobación/rechazo conjunto de reactivaciones */}
      {confirmReactivar && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
             data-testid="modal-confirm-reactivaciones-masivas">
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full space-y-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-7 h-7 text-amber-600 flex-shrink-0" />
              <div>
                <h4 className="font-black text-lg">
                  {confirmReactivar.decision === 'APROBADA'
                    ? 'Aprobar todas las reactivaciones pendientes'
                    : 'Rechazar todas las reactivaciones pendientes'}
                </h4>
                <p className="text-sm text-slate-700 mt-2">
                  Vas a {confirmReactivar.decision === 'APROBADA' ? 'reactivar' : 'rechazar la reactivación de'}{' '}
                  <b className="text-amber-800 text-lg">{confirmReactivar.count}</b>{' '}
                  suscripciones que hoy están en <b>BAJA</b>.
                </p>
                {confirmReactivar.decision === 'APROBADA' && (
                  <p className="text-xs text-slate-600 mt-2 bg-amber-50 border border-amber-200 rounded-lg p-2">
                    Estas suscripciones volverán a estar <b>ACTIVAS/PIPELINE</b> y se retomará su facturación.
                    Esta aprobación conjunta constituye la <b>confirmación expresa</b> de la reactivación.
                  </p>
                )}
                <p className="text-xs text-slate-500 mt-2">
                  Queda registrado en auditoría quién ha decidido, cuándo, cuántas filas y que el modo es conjunto.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <button className="btn-secondary" onClick={() => setConfirmReactivar(null)}
                      data-testid="modal-reactivaciones-cancelar">Cancelar</button>
              <button
                className={confirmReactivar.decision === 'APROBADA' ? 'btn-primary bg-amber-600 hover:bg-amber-700' : 'btn-danger'}
                onClick={ejecutarReactivacionMasiva}
                data-testid="modal-reactivaciones-confirmar">
                Confirmar {confirmReactivar.decision === 'APROBADA' ? 'aprobación' : 'rechazo'} de {confirmReactivar.count} filas
              </button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

function ContextoFuncional({ ctx }) {
  if (!ctx || Object.keys(ctx).length === 0) {
    return <span className="text-slate-400">—</span>;
  }
  const cli = ctx.cliente_operativo;
  const dev = ctx.dispositivo;
  const prod = ctx.producto_saas;
  return (
    <div className="space-y-0.5">
      {cli && (
        <div><b className="text-slate-700">{cli.razon_social || cli.id || '—'}</b>
          {ctx.grupo?.nombre && <span className="text-slate-500"> · {ctx.grupo.nombre}</span>}
        </div>
      )}
      {dev && (
        <div className="text-slate-600">
          {dev.matricula && <span className="font-mono">🚗 {dev.matricula}</span>}
          {dev.matricula && dev.id_pack && <span> · </span>}
          {dev.id_pack && <span className="font-mono">📦 {dev.id_pack}</span>}
          {dev.serial_geotab && <span> · <span className="font-mono">SN {dev.serial_geotab}</span></span>}
        </div>
      )}
      {prod?.nombre && <div className="text-slate-500">{prod.nombre}{prod.sku && ` (${prod.sku})`}</div>}
    </div>
  );
}

function CambiosResumen({ diferencias, ctx }) {
  if (!diferencias.length) return <span className="text-slate-400">— (sellado)</span>;
  // Priorizar cambios legibles: estado, precio, fechas, frecuencia.
  const dEstado = diferencias.find(d => d.campo === 'suscripcion.estado');
  const dTarifa = diferencias.find(d => d.campo === 'precio.tarifa_activa');
  const dFinCobro = diferencias.find(d => d.campo === 'suscripcion.fecha_fin_cobro');
  const dLimite = diferencias.find(d => d.campo === 'suscripcion.fecha_limite_cobro');
  const dFrec = diferencias.find(d => d.campo === 'periodo.frecuencia_origen'
                                     || d.campo === 'precio.frecuencia_origen');
  const otros = diferencias
    .filter(d => ![
      'suscripcion.estado', 'precio.tarifa_activa',
      'suscripcion.fecha_fin_cobro', 'suscripcion.fecha_limite_cobro',
      'periodo.frecuencia_origen', 'precio.frecuencia_origen',
    ].includes(d.campo));
  const fmt = (v) => v === null || v === undefined || v === '' ? '—' : String(v);
  return (
    <div className="space-y-0.5">
      {dEstado && (
        <div><b>Estado:</b> {fmt(dEstado.anterior)} → <b className="text-emerald-700">{fmt(dEstado.nuevo)}</b></div>
      )}
      {dTarifa && (
        <div><b>Tarifa mensual:</b> {fmt(dTarifa.anterior)} € → <b className="text-emerald-700">{fmt(dTarifa.nuevo)} €</b>
          {ctx?.precio?.frecuencia && <span className="text-slate-500"> ({ctx.precio.frecuencia})</span>}
        </div>
      )}
      {(dFinCobro || dLimite) && (
        <div><b>Fecha:</b> {dLimite && `activación ${fmt(dLimite.anterior)} → ${fmt(dLimite.nuevo)}`}
          {dLimite && dFinCobro && ' · '}
          {dFinCobro && `fin cobro ${fmt(dFinCobro.anterior)} → ${fmt(dFinCobro.nuevo)}`}
        </div>
      )}
      {dFrec && (
        <div><b>Frecuencia:</b> {fmt(dFrec.anterior)} → {fmt(dFrec.nuevo)}</div>
      )}
      {!!otros.length && (
        <div className="text-slate-500 text-[11px]">
          + {otros.length} campo{otros.length > 1 ? 's' : ''} más ({otros.map(o => o.campo.split('.').pop()).join(', ')})
        </div>
      )}
    </div>
  );
}

function fmtAsocStock(df, k) {
  if (df.campo === 'ASOCIACION_STOCK') return df[k] ? 'asociado' : 'no asociado';
  return String(df[k] ?? '—');
}

function DecisionBadge({ d }) {
  const tone = d === 'APROBADA' ? 'bg-emerald-100 text-emerald-800'
    : d === 'RECHAZADA' ? 'bg-rose-100 text-rose-800'
    : 'bg-slate-100 text-slate-700';
  return <span className={`text-xs px-2 py-0.5 rounded ${tone}`}>{d || 'PENDIENTE'}</span>;
}

function Confirmacion({ sesion, consecuencias, confirm, setConfirm, onCommit, loading, puedeCommit, onVolver }) {
  return (
    <Card>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-black text-lg">Confirmación · Aplicar cambios aprobados</h3>
        <button className="btn-secondary" onClick={onVolver}>Volver a revisión</button>
      </div>
      <div className="bg-slate-50 border rounded-xl p-4 text-sm mb-4" data-testid="reco-consecuencias-commit">
        <p><b>Consecuencias al aplicar:</b></p>
        <ul className="list-disc pl-6 mt-1">
          <li>Nuevas suscripciones a crear: <b>{consecuencias?.nuevas || 0}</b></li>
          <li>Suscripciones a actualizar: <b>{consecuencias?.actualizaciones || 0}</b></li>
          <li>Reconciliaciones (sellado de hash): <b>{consecuencias?.selladas || 0}</b></li>
          <li>Filas rechazadas (no se tocan): <b>{consecuencias?.rechazadas || 0}</b></li>
          <li>Pendientes de decisión: <b>{consecuencias?.pendientes || 0}</b> {consecuencias?.pendientes > 0 && '⚠'}</li>
          <li>Bloqueadas por reglas: <b>{consecuencias?.bloqueadas || 0}</b> {consecuencias?.bloqueadas > 0 && '⚠'}</li>
        </ul>
      </div>
      <p className="text-sm text-slate-500 mb-2">Solo <b>super_admin</b>. Escribe exactamente <b>IMPORTAR MAESTRO</b> para confirmar. No es necesario volver a aportar el archivo: se aplicarán las decisiones ya aprobadas.</p>
      <div className="flex flex-wrap gap-2 items-center">
        <input data-testid="reco-txt-confirm" className="input max-w-xs" value={confirm}
          onChange={e => setConfirm(e.target.value)} placeholder="IMPORTAR MAESTRO" />
        <button data-testid="reco-btn-commit" className="btn-primary"
          disabled={loading || !puedeCommit} onClick={onCommit}>
          {loading ? 'Aplicando…' : 'Aplicar cambios aprobados'}
        </button>
      </div>
    </Card>
  );
}

function Informe({ result, sesion, onDescargar }) {
  const ok = result.estado_ejecucion === 'COMPLETADA';
  return (
    <Card>
      <div className="flex items-center gap-2 mb-3">
        {ok ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <AlertTriangle className="w-5 h-5 text-amber-600" />}
        <h3 className="font-black text-lg">Informe de resultados</h3>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="reco-informe-stats">
        <Mini label="Creadas" value={result.creadas} tid="creadas" />
        <Mini label="Actualizadas" value={result.actualizadas} tid="actualizadas" />
        <Mini label="Selladas" value={result.selladas} tid="selladas" />
        <Mini label="Omitidas" value={result.omitidas_estado_cambio} tid="omitidas" />
        <Mini label="Fallidas" value={result.falladas} tid="falladas" />
      </div>
      {!!result.no_aplicadas?.length && (
        <div className="mt-4 border rounded-xl p-3" data-testid="reco-no-aplicadas">
          <p className="font-bold text-sm mb-2">Filas no aplicadas (requieren reanálisis)</p>
          <table className="table"><thead><tr><th>Fila</th><th>Motivo</th></tr></thead>
            <tbody>{result.no_aplicadas.map((x, i) => (
              <tr key={i}><td className="font-mono text-xs">{x.fila_origen}</td><td>{x.motivo}</td></tr>
            ))}</tbody></table>
        </div>
      )}
      <p className="text-sm text-slate-500 mt-3" data-testid="reco-import-id">
        Import ID: <b>{result.import_id}</b> · Estado: <b>{result.estado_ejecucion}</b>
      </p>
      {result.ultima_excepcion && (
        <div className="mt-3 text-sm bg-rose-50 border border-rose-200 rounded-xl p-3" data-testid="reco-error">
          Última excepción: {result.ultima_excepcion}
        </div>
      )}
      <div className="flex gap-2 mt-4">
        <button className="btn-secondary" onClick={() => onDescargar('json')} data-testid="reco-btn-descargar-informe-json">
          <Download className="w-4 h-4 inline" /> Descargar JSON
        </button>
        <button className="btn-secondary" onClick={() => onDescargar('csv')} data-testid="reco-btn-descargar-informe-csv">
          <Download className="w-4 h-4 inline" /> Descargar CSV
        </button>
      </div>
    </Card>
  );
}

// --------------------------------------------------------------------------
// Primitivos
// --------------------------------------------------------------------------
function Card({ children }) { return <div className="bg-white border rounded-2xl p-5">{children}</div>; }
function CardHeader({ icon, title, desc }) {
  return <div className="flex items-start gap-3"><span className="mt-1">{icon}</span><div><h2 className="text-xl font-black">{title}</h2><p className="text-sm text-slate-500 mt-1">{desc}</p></div></div>;
}
function Field({ label, children }) { return <label className="block mb-3"><span className="text-xs font-bold text-slate-600 block mb-1.5">{label}</span>{children}</label>; }
function Mini({ label, value, tid }) { return <div className="border rounded-xl p-3" data-testid={`reco-mini-${tid}`}><div className="text-xs text-slate-500">{label}</div><div className="text-2xl font-black">{value ?? 0}</div></div>; }
function StatBox({ label, value, tone, tid, selected = false, onClick }) {
  const cls = ({
    slate: 'bg-slate-50 border-slate-200 text-slate-800',
    amber: 'bg-amber-50 border-amber-300 text-amber-900',
    emerald: 'bg-emerald-50 border-emerald-300 text-emerald-800',
    rose: 'bg-rose-50 border-rose-300 text-rose-800',
  })[tone] || 'bg-white border-slate-200';
  const selCls = selected ? 'ring-2 ring-offset-2 ring-[#0a6640] scale-[1.02]' : '';
  const cursorCls = onClick ? 'cursor-pointer hover:shadow-md transition' : '';
  return <button type="button" onClick={onClick}
      className={`text-left w-full border rounded-xl p-3 ${cls} ${selCls} ${cursorCls}`}
      data-testid={`reco-count-${tid}`} aria-pressed={selected}>
    <div className="text-xs opacity-70">{label}</div>
    <div className="text-2xl font-black">{value ?? 0}</div>
    {selected && <div className="text-[10px] mt-0.5 opacity-70">✓ Filtro activo</div>}
  </button>;
}
function Simple({ headers, rows, ...rest }) {
  return <div className="overflow-auto" {...rest}><table className="table"><thead><tr>{headers.map(h => <th key={h}>{h}</th>)}</tr></thead>
    <tbody>{rows.length ? rows.map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j}>{c}</td>)}</tr>) : <tr><td colSpan={headers.length} className="text-center py-6 text-slate-400">Sin resultados</td></tr>}</tbody></table></div>;
}
